/**
 * NavBreakdown — the terms NAV adds up, at two dates
 * =================================================
 * Brian, 23 September 2026: *"The NAV on every page doesnt tie out"* — so the
 * panel shows every term NAV adds, and names liabilities exactly when NAV
 * subtracts some (21 October 2026: *"please remove the liabilities box
 * now"*, D-275).
 *
 * Usability review of 23 October 2026, item 20: with a comparison date
 * picked, the same terms at that date and the change sit beside them, so
 * month-end against today needs no second tab.
 */
import React from "react";
import { InfoTip } from "../../components/PageTools";
import { amount, day, money } from "../../components/format";
import { hasLiabilities } from "../../components/portfolioDashboardData";
import { delta } from "./navLogic";

const whole = (v) => money(v, { whole: true });
const signed = (v) => (v > 0 ? `+${whole(v)}` : whole(v));

export default function NavBreakdown({ summary, compare, asOfDate, compareDate, compareLoading, fxRates }) {
  const s = summary || {};
  const c = compare?.summary || null;
  const showCompare = Boolean(compareDate);
  const lines = [
    ["Invested assets", "total_positions"],
    ["Cash", "total_cash"],
    ["Accrued interest", "total_accrued_interest"],
    ["Gross assets", "gross_assets", "subtotal"],
    ...(hasLiabilities(s) || (c && hasLiabilities(c)) ? [["Less: liabilities", "total_liabilities"]] : []),
    ["Net asset value", "nav", "total"],
  ];
  const rates = Object.entries(fxRates || {}).filter(([ccy]) => ccy !== "USD");
  return (
    <section className="nvp-panel nvp-breakdown-panel" aria-labelledby="nvp-breakdown-h">
      <div className="nvp-panel-head">
        <h2 id="nvp-breakdown-h">NAV breakdown</h2>
        <p>From gross assets to net asset value</p>
      </div>
      <table className="nvp-bridge">
        <thead>
          <tr>
            <th scope="col"><span className="nvp-sr">Line</span></th>
            <th scope="col">{day(asOfDate)}</th>
            {showCompare && <th scope="col">{day(compareDate)}</th>}
            {showCompare && <th scope="col">Change</th>}
          </tr>
        </thead>
        <tbody>
          {lines.map(([label, key, kind]) => (
            <tr key={key} className={kind ? `nvp-bridge-${kind}` : ""}>
              <th scope="row">{label}</th>
              <td>{whole(s[key])}</td>
              {showCompare && <td>{c ? whole(c[key]) : compareLoading ? "…" : "n/a"}</td>}
              {showCompare && <td>{c ? signed(delta(s[key], c[key])) : ""}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      {rates.length > 0 && (
        <div className="nvp-fx">
          <span>
            Exchange rates, US$ per unit{" "}
            <InfoTip align="right" label="Where the exchange rates come from">
              <p>The Riksbank’s fixing for each currency on or before the valuation date.</p>
            </InfoTip>
          </span>
          {rates.map(([ccy, rate]) => (
            <span key={ccy}>{ccy} <b>{amount(rate, 4)}</b></span>
          ))}
        </div>
      )}
    </section>
  );
}
